import { z } from 'zod';

/**
 * The review page's two views (#7656): the queue a reviewer works, and the claim
 * aging view that says which suppliers to chase.
 */
export const REVIEW_VIEWS = ['queue', 'aging'] as const;
export type ReviewView = (typeof REVIEW_VIEWS)[number];

/**
 * URL state for the review page. The open email is in the URL so a specific one
 * can be linked to — from Browse ("Open in Review"), from Slack — and so a reload
 * puts the reviewer back on the item they were judging. Any email opens, not only
 * a queue member: that is how an email the classifier let through is still
 * answered, which the retired 849 inbox used to be for.
 *
 * `view` is validated but not strict: `.catch` turns an unknown value into the
 * queue instead of rejecting the whole route.
 */
export const reviewSearchSchema = z.object({
  view: z.enum(REVIEW_VIEWS).catch('queue').optional(),
  emailId: z.string().optional(),
});

export type ReviewSearch = z.infer<typeof reviewSearchSchema>;

/**
 * Where an old labeling-page URL now lives (#7656). The labeling page's Review,
 * Matches and Claim aging modes moved to the review page; those links are in
 * bookmarks and Slack history. Null for everything the labeling page still shows.
 *
 * Only a Review link carries its open email across: a Matches link's email was a
 * response being line-matched, which has no equivalent to open.
 */
export function legacyLabelingModeTarget(params: {
  mode: string | undefined;
  emailId: string | undefined;
}): ReviewSearch | null {
  if (params.mode === 'review') {
    return params.emailId
      ? { view: 'queue', emailId: params.emailId }
      : { view: 'queue' };
  }
  if (params.mode === 'matches') return { view: 'queue' };
  if (params.mode === 'aging') return { view: 'aging' };
  return null;
}
